import type { PermissionKey } from "@nmt/shared";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Link, Navigate, Route, Routes, useParams } from "react-router";
import { Shell } from "./components/Shell";
import { Loading } from "./components/ui";
import { ContactDetailPage, ContactsPage } from "./pages/Contacts";
import { CalendarPage } from "./pages/Calendar";
import { MapPage } from "./pages/Map";
import { FredInboxPage } from "./pages/FredInbox";
import { AssistantPage } from "./pages/Assistant";
import { Dashboard } from "./pages/Dashboard";
import { JobDetailPage } from "./pages/JobDetail";
import { JobsPage } from "./pages/Jobs";
import { ProposalPage } from "./pages/Proposal";
import { PublicProposalPage } from "./pages/PublicProposal";
import { PublicWorkOrderPage } from "./pages/PublicWorkOrder";
import { WorkOrdersPage } from "./pages/WorkOrders";
import { MaterialOrderPage, MaterialOrdersPage } from "./pages/Materials";
import { DocumentsPage } from "./pages/Documents";
import { SignIn } from "./pages/SignIn";
import { TasksPage } from "./pages/Tasks";
import { CameraPage } from "./pages/Camera";
import { ActivityPage } from "./pages/settings/Activity";
import { CompanySettings } from "./pages/settings/Company";
import { ContactTypesSettings } from "./pages/settings/ContactTypes";
import { LeadsSettings } from "./pages/settings/Leads";
import { ListsSettings } from "./pages/settings/Lists";
import { RecycleBinSettings } from "./pages/settings/RecycleBin";
import { RolesPage } from "./pages/settings/Roles";
import { SessionsSettings } from "./pages/settings/Sessions";
import { SettingsLayout } from "./pages/settings/SettingsLayout";
import { ProfilePage } from "./pages/Profile";
import { UserDetailPage } from "./pages/settings/UserDetail";
import { UsersPage } from "./pages/settings/Users";
import { WorkflowsSettings } from "./pages/settings/Workflows";
import { WorkOrderSettings } from "./pages/settings/WorkOrderSettings";
import { CalendarSettings } from "./pages/settings/CalendarSettings";
import { EmailTemplates } from "./pages/settings/EmailTemplates";
import { RetailPricing } from "./pages/settings/RetailPricing";
import { MaterialSettings } from "./pages/settings/MaterialSettings";
import { ImportSettings } from "./pages/settings/Import";
import { CommsSettings } from "./pages/settings/CommsSettings";
import { QuickBooksSettings } from "./pages/settings/QuickBooksSettings";
import { SyncSettings } from "./pages/settings/SyncSettings";
import { CommsPage } from "./pages/Comms";
import { ReceiptsPage } from "./pages/Receipts";
import { ReceiptPage } from "./pages/ReceiptPage";
import { PublicPayPage } from "./pages/PublicPay";
import { PublicStatementPage } from "./pages/PublicStatement";
import { ReportsPage } from "./pages/Reports";
import { PublicChangeOrderPage } from "./pages/ChangeOrders";
import { PublicSurveyPage } from "./pages/Survey";
import { SubPortalPage } from "./pages/SubPortal";
import { AutomationsPage } from "./pages/settings/Automations";
import { AiSettings } from "./pages/settings/AiSettings";
import { registerServiceWorker } from "./lib/push";
import { SessionProvider, useSession } from "./session";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

function Guard({ needs, children }: { needs: PermissionKey | PermissionKey[]; children: ReactNode }) {
  const { can } = useSession();
  if (!can(...(Array.isArray(needs) ? needs : [needs]))) {
    return (
      <div className="py-10">
        <h1 className="page-title">No access</h1>
        <p className="mt-2 text-ink-soft">Your role doesn't include this page. Ask an admin if you need it.</p>
        <Link to="/" className="mt-4 inline-block font-semibold text-brand">
          Go to the dashboard
        </Link>
      </div>
    );
  }
  return children;
}

function CustomerRedirect() {
  const { id } = useParams();
  return <Navigate to={`/contacts/${id}`} replace />;
}

function App() {
  const { me, loading } = useSession();
  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  }
  if (!me) return <SignIn />;
  const view = (el: ReactNode) => <Guard needs="customers.view">{el}</Guard>;
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={view(<Dashboard />)} />
        <Route path="jobs" element={view(<JobsPage />)} />
        <Route path="jobs/:id" element={view(<JobDetailPage />)} />
        {/* Customers became contacts; old links still work. */}
        <Route path="customers" element={<Navigate to="/contacts?type=category:customer" replace />} />
        <Route path="customers/:id" element={<CustomerRedirect />} />
        <Route path="calendar" element={view(<CalendarPage />)} />
        <Route path="map" element={view(<MapPage />)} />
        <Route path="fred-inbox" element={<Guard needs="fred.use"><FredInboxPage /></Guard>} />
        <Route path="assistant" element={<Guard needs="fred.use"><AssistantPage /></Guard>} />
        <Route path="proposals/:id" element={<Guard needs="money.view">{<ProposalPage />}</Guard>} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="camera" element={<Guard needs="files.upload"><CameraPage /></Guard>} />
        <Route path="work-orders" element={view(<WorkOrdersPage />)} />
        <Route path="material-orders" element={view(<MaterialOrdersPage />)} />
        <Route path="documents" element={view(<DocumentsPage />)} />
        <Route path="material-orders/:id" element={view(<MaterialOrderPage />)} />
        <Route path="receipts" element={<Guard needs="receipts.submit">{<ReceiptsPage />}</Guard>} />
        <Route path="receipts/:id" element={<Guard needs="receipts.submit">{<ReceiptPage />}</Guard>} />
        <Route path="reports" element={<Guard needs={["reports.overview", "reports.leaderboard", "reports.satisfaction", "reports.closeRate", "reports.aging", "reports.pipeline", "reports.leadSources", "reports.jobProfit"]}><ReportsPage /></Guard>} />
        <Route path="inbox" element={<Guard needs="comms.view"><CommsPage /></Guard>} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="contacts" element={view(<ContactsPage />)} />
        <Route path="contacts/:id" element={view(<ContactDetailPage />)} />
        <Route path="settings" element={<SettingsLayout />}>
          <Route path="company" element={<Guard needs="settings.manage"><CompanySettings /></Guard>} />
          <Route path="users" element={<Guard needs="users.manage"><UsersPage /></Guard>} />
          <Route path="users/:id" element={<Guard needs="users.manage"><UserDetailPage /></Guard>} />
          <Route path="roles" element={<Guard needs="roles.manage"><RolesPage /></Guard>} />
          <Route path="workflows" element={<Guard needs="settings.manage"><WorkflowsSettings /></Guard>} />
          <Route path="contact-types" element={<Guard needs="settings.manage"><ContactTypesSettings /></Guard>} />
          <Route path="lists" element={<Guard needs="settings.manage"><ListsSettings /></Guard>} />
          <Route path="work-orders" element={<Guard needs="settings.manage"><WorkOrderSettings /></Guard>} />
          <Route path="calendar" element={<Guard needs="settings.manage"><CalendarSettings /></Guard>} />
          <Route path="email-templates" element={<Guard needs="settings.manage"><EmailTemplates /></Guard>} />
          <Route path="retail-pricing" element={<Guard needs="settings.manage"><RetailPricing /></Guard>} />
          <Route path="materials" element={<Guard needs="settings.manage"><MaterialSettings /></Guard>} />
          <Route path="automations" element={<Guard needs="settings.manage"><AutomationsPage /></Guard>} />
          <Route path="ai" element={<Guard needs="settings.manage"><AiSettings /></Guard>} />
          <Route path="leads" element={<Guard needs="settings.manage"><LeadsSettings /></Guard>} />
          <Route path="sync" element={<Guard needs="settings.manage"><SyncSettings /></Guard>} />
          <Route path="quickbooks" element={<Guard needs="settings.manage"><QuickBooksSettings /></Guard>} />
          <Route path="comms" element={<Guard needs="settings.manage"><CommsSettings /></Guard>} />
          <Route path="import" element={<Guard needs="import.run"><ImportSettings /></Guard>} />
          <Route path="sessions" element={<Guard needs="users.manage"><SessionsSettings /></Guard>} />
          <Route path="activity" element={<Guard needs="audit.view"><ActivityPage /></Guard>} />
          <Route path="recycle-bin" element={<Guard needs="records.delete"><RecycleBinSettings /></Guard>} />
          {/* Moved to My profile; old links still work. */}
          <Route path="profile" element={<Navigate to="/profile" replace />} />
          <Route path="notifications" element={<Navigate to="/profile" replace />} />
          <Route path="signature" element={<Navigate to="/profile" replace />} />
        </Route>
        <Route path="*" element={<p className="py-10 text-ink-soft">That page doesn't exist.</p>} />
      </Route>
    </Routes>
  );
}

registerServiceWorker();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* A customer's proposal link and a sub's work order link work without signing in. */}
        {window.location.pathname.startsWith("/p/") ? (
          <PublicProposalPage />
        ) : window.location.pathname.startsWith("/wo/") ? (
          <PublicWorkOrderPage />
        ) : window.location.pathname.startsWith("/pay/") ? (
          <PublicPayPage />
        ) : window.location.pathname.startsWith("/statement/") ? (
          <PublicStatementPage />
        ) : window.location.pathname.startsWith("/co/") ? (
          <PublicChangeOrderPage />
        ) : window.location.pathname.startsWith("/s/") ? (
          <PublicSurveyPage />
        ) : window.location.pathname.startsWith("/sub/") ? (
          <SubPortalPage />
        ) : (
          <SessionProvider>
            <App />
          </SessionProvider>
        )}
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
